import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import TextField from "@mui/material/TextField";
import { connect, getIn } from "formik";
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Popper from "@mui/material/Popper";
import "../../../../pages/admin/admin.css";
import { Chip, Typography, FormHelperText, FormControl, Grid, Box, Button, Tooltip } from "@mui/material";
import CustomizedCheckbox from "../CustomCheckBox";
import Avatar from '@mui/material/Avatar';
import { stringAvatar } from "../Avatar";

const DropDownIcon = () => {
    return (
        <div style={{ display: "flex", alignContent: "center", marginTop: "7px", marginRight: "4px", }}>
            <img
                src={`${process.env.PUBLIC_URL}/icons/downArrowDropDown.svg`}
                alt="edit"
            ></img>
        </div>
    )
};

const CustomAutoCompleteSubAdmin = connect(
    ({
        Datas,
        placeholder,
        formik,
        name,
        labelHeader,
        optionLabelName,
        optionId,
        objId = 'id',
        mode,
        autocompleteTextFieldWidth,
        showAvatar,
        showCheckBox,
        showAvatarInChip,
        temporaryMode,
        multiple,
        showBottomButton,
        buttonName,
        small,
        rightradius,
        leftradius,
        retry,
        disabled,
        disableOptionsNotInDatas,
        subAdminApiList,
        removeDuplicates,
        ...additionalProps
    }) => {
        const [limit, setLimit] = useState(10);
        const listRef = useRef(null);
        const [tempMode, setTempMode] = useState(temporaryMode ? temporaryMode : "temp");
        const [inputValue, setInputValue] = useState("");
        const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
        const refContainer = useRef();
        const [filteredData, setFilteredData] = useState([]);
        const [oldArray, setOldArray] = useState([]);


        // Memoize expensive computations
        const adminValuesList = useMemo(() => {
            if (!disableOptionsNotInDatas) return [];

            try {
                const adminValues = [];
                const selectedInitialValues = getIn(formik?.initialValues, name) || [];

                selectedInitialValues.forEach((element) => {
                    const skillIndex = subAdminApiList?.findIndex((option) => option["id"] === element["id"]);
                    if (skillIndex === -1) {
                        adminValues.push(element);
                    }
                });

                return adminValues;
            } catch (e) {
                console.log("Error in Admin Values List", e);
                return [];
            }
        }, [disableOptionsNotInDatas, subAdminApiList, formik?.initialValues, name]);

        const combinedOptions = useMemo(() => {
            try {
                let newDataList = adminValuesList && adminValuesList.length > 0 ? [...Datas, ...adminValuesList] : Datas;

                if (removeDuplicates && newDataList && newDataList.length > 0) {
                    newDataList = newDataList.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i);
                }

                return newDataList;
            } catch (e) {
                return Datas;
            }
        }, [adminValuesList, Datas, removeDuplicates]);

        // Use memoized data
        const actualDatas = useMemo(() =>
            disableOptionsNotInDatas ? combinedOptions : Datas,
            [disableOptionsNotInDatas, combinedOptions, Datas]);

        const error = getIn(formik.errors, name);

        const getTouchedStatus = () => {
            let touched = getIn(formik.touched, name);
            if (touched) {
                if (multiple) {
                    if (touched.length === 0) {
                        return true
                    } else {
                        return touched
                    }
                } else {
                    return touched
                }
            } else {
                return touched
            }
        }

        const touch = getTouchedStatus();
        const selected = getIn(formik?.values, name) || [];
        let [selectAll, setSelectAll] = useState(selected?.length === actualDatas?.length);

        const handleScroll = () => {
            const l = limit;
            setLimit(l + 5);
        }

        const handleClose = () => {
            setLimit(10);
        }

        const handleResize = () => {
            if (refContainer.current) {
                setDimensions({
                    width: refContainer.current.offsetWidth,
                    height: refContainer.current.offsetHeight,
                });
            }
        }

        const makeNewArray = (oldData) => {
            if (oldArray === null || oldArray.length < 1) {
                return oldData;
            } else {
                const newnew = oldArray.concat(oldData);
                const uniqueIds = [];
                const result = newnew.filter(obj => {
                    if (!uniqueIds.includes(obj[objId])) {
                        uniqueIds.push(obj[objId]);
                        return true;
                    }
                    return false;
                })
                return result;
            }
        }

        useEffect(() => {
            if (refContainer.current) {
                setDimensions({
                    width: refContainer.current.offsetWidth,
                    height: refContainer.current.offsetHeight,
                });
            }
            window.addEventListener("resize", handleResize, true);

            return () => {
                window.removeEventListener("resize", handleResize);
            };
        }, []);

        // Initialize oldArray
        useEffect(() => {
            if (selected && selected.length > 0) {
                setOldArray(selected);
            }
        }, []);

        function changeMode() {
            if (mode !== "Add") {
                setTempMode("Edit");
            }

            if (additionalProps.onChangeValue) {
                additionalProps.onChangeValue.handleChangeDatasList(
                    additionalProps.onChangeValue.index,
                    selected,
                    additionalProps.onChangeValue.createNewDatasList
                );
            }
        }

        // Update filtered data whenever the input value changes
        useEffect(() => {
            if (inputValue) {
                const filtered = actualDatas.filter(item =>
                    item[optionLabelName]?.toLowerCase().includes(inputValue.toLowerCase())
                );
                setFilteredData(filtered);
            } else {
                setFilteredData([]);
            }
        }, [inputValue, actualDatas, optionLabelName]);

        // Function to handle select all with event prevention
        const handleSelectAll = useCallback((e) => {
            // Make sure we stop event propagation if this is an event
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }

            const filteredDatas = inputValue
                ? actualDatas.filter(item => item[optionLabelName].toLowerCase().includes(inputValue.toLowerCase()))
                : actualDatas;
            const newArray = makeNewArray(filteredDatas);
            setOldArray(newArray);

            formik.setFieldValue(name, newArray);
            setSelectAll(true);

            if (additionalProps.onChangeValue) {
                additionalProps.onChangeValue.handleChangeDatasList(
                    additionalProps.onChangeValue.index,
                    newArray,
                    additionalProps.onChangeValue.createNewDatasList
                );
            }
        }, [inputValue, actualDatas, optionLabelName, name, formik, additionalProps]);

        // Function to handle clear filtered items with event prevention
        const handleClearFiltered = useCallback((e) => {
            // Make sure we stop event propagation
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }

            // If no filter or no selected items, do nothing
            if (!selected || selected.length === 0) return;

            // Get the current filtered items that match the search text
            const filtered = actualDatas.filter(item =>
                item[optionLabelName].toLowerCase().includes(inputValue.toLowerCase())
            );

            // If no items match the filter, do nothing
            if (filtered.length === 0) return;

            // Create a set of IDs for faster lookup
            const filteredIds = new Set(filtered.map(item => item[objId]));

            // Remove matching items from selection (keep only non-filtered items)
            const newSelected = selected.filter(item => !filteredIds.has(item[objId]));

            // Update the form with the new selection
            formik.setFieldValue(name, newSelected);

            // Update other state as needed
            setSelectAll(false);
            setOldArray(newSelected);

            // Call any change handlers
            if (additionalProps.onChangeValue) {
                additionalProps.onChangeValue.handleChangeDatasList(
                    additionalProps.onChangeValue.index,
                    newSelected,
                    additionalProps.onChangeValue.createNewDatasList
                );
            }
        }, [selected, actualDatas, optionLabelName, inputValue, objId, formik, name, additionalProps]);

        // Memoize fixed styling objects
        const listItemStyle = useMemo(
            () => ({
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "16px",
                color: '#212121',
                paddingLeft: "6px"
            }),
            []
        );

        const styleFieldHeader = useMemo(
            () => ({
                display: "flex",
                fontSize: "12px",
                fontWeigth: 400,
                color: '#212121',
                lineHeight: "20px",
                paddingLeft: "5px",
            }),
            []
        );

        // Memoize PopperMy component for performance
        const PopperMy = useMemo(
            () => (props, option) => (
                <Popper {...props}
                    sx={{
                        "& .MuiAutocomplete-listbox": {
                            padding: "0px",
                            "& li": {
                                height: ((name === "dispositions" || additionalProps?.postCallWork || additionalProps?.retry) ? "60px !important" : "35px !important"),
                                padding: '0px',
                                paddingLeft: (option[optionLabelName] !== "Button") ? '5px' : '0px',
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "#FFFFFF !important",
                                marginLeft: "-1px",
                                maxWidth: "100% !important",
                                wordBreak: "break-all !important",
                                whiteSpace: "normal !important",
                            },
                            "& :hover": {
                                backgroundColor: "#FFFFFF !important"
                            },
                        },
                        '& .MuiAutocomplete-paper': {
                            marginTop: "0px",
                            marginBottom: "5px",
                            boxShadow: 'none !important',
                            border: "1px solid #E5E5E5",
                            borderRadius: "0px",
                            borderBottom: mode === "Edit" ? '0px' : "1px solid #E5E5E5",
                            borderRight: mode === "Edit" ? '0px' : "1px solid #E5E5E5",
                            borderLeft: mode === "Edit" ? '0px' : "1px solid #E5E5E5",
                            borderTop: mode === "Edit" ? "0px" : "1px solid #E5E5E5",
                            maxHeight: "275px !important",
                            overflow: "hidden !important",
                            "& .MuiAutocomplete-noOptions": {
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: "14px",
                                lineHeight: "16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#212121"
                            }
                        },
                    }} placement='bottom' />
            ),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [],
        );

        return (
            <div>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                    {labelHeader &&
                        <Typography
                            htmlFor={name}
                            fontSize='12px'
                            fontWeight={400}
                            lineHeight='14px'
                            marginBottom={'6px'}
                            marginLeft='1px'
                            color={disabled ? "#99A0A8" : "#212121"}
                        >
                            {labelHeader}
                        </Typography>
                    }

                    <Box
                        sx={(mode === "Edit" && tempMode === "Edit") ? {
                            border: "1px solid #E5E5E5",
                            borderRadius: "8px",
                            paddingTop: "8px",
                            paddingLeft: "15px",
                            paddingBottom: "8px",
                            borderBottomRightRadius: 0,
                            borderBottomLeftRadius: 0,
                        } : {}}
                    >
                        {mode === "Edit" && tempMode === "Edit" &&
                            <label
                                sx={mode === "Edit" && tempMode === "Edit" && {
                                    color: "#212121",
                                    fontWeight: 400,
                                    fontSize: "14px",
                                    marginLeft: "10px",
                                }}
                            >
                                {(selected?.length < 10) ?
                                    ("0" + (selected?.length > 0 ? selected?.length : "")) :
                                    selected?.length
                                } Selected...
                            </label>
                        }
                    </Box>

                    <Box
                        sx={(mode === "Edit" && tempMode === "Edit") ? {
                            border: "1px solid #E5E5E5",
                            borderRadius: "8px",
                            paddingTop: "15px",
                            paddingLeft: "15px",
                            paddingBottom: "305px",
                            borderTop: 0,
                            paddingRight: "15px",
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            height: mode === "Add" ? "" : "344px"
                        } : {}}
                    >
                        <Autocomplete
                            ref={refContainer}
                            sx={{
                                '& .MuiAutocomplete-listbox': {
                                    scrollbarColor: 'rgb(190, 190, 190) rgb(240, 240, 240)',
                                    scrollbarWidth: 'thin',
                                },
                                "& .MuiAutocomplete-root": {
                                    "& .MuiAutocomplete-noOptions": {
                                        color: "red"
                                    }
                                },
                                "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                                    maxWidth: !retry ? (rightradius ? '280px !important' : leftradius ? '170px !important' : small ? '100px !important' : null) : retry ? (rightradius ? '380px !important' : leftradius ? '170px !important' : small ? '100px !important' : null) : null,
                                    borderRadius: (rightradius ? "0px 8px 8px 0px !important" : leftradius ? "8px 0px 0px 8px !important" : null),
                                },
                                "& .MuiAutocomplete-popupIndicatorOpen": {
                                    transform: "none",
                                    marginRight: "0px",
                                    padding: "0px"
                                },
                            }}
                            popupIcon={additionalProps.popupIcon || <DropDownIcon />}
                            clearIcon={""}
                            noOptionsText={
                                <>
                                    {(!showBottomButton) ?
                                        "No Match Result Found !!" :
                                        <div>
                                            <div>No Match Result Found !!</div>
                                            <Button
                                                sx={{ marginLeft: '-9px', marginTop: '10px' }}
                                                onClick={() => { additionalProps.bottomButtonFunction() }}
                                            >
                                                {buttonName}
                                            </Button>
                                        </div>
                                    }
                                </>
                            }
                            autoComplete={true}
                            disabled={disabled}
                            className="ozcw-scrollbar"
                            multiple={multiple}
                            size="small"
                            id={name}
                            options={actualDatas}
                            value={selected}
                            // Key Fix #1: Keep input value when selecting options
                            {...multiple ? { inputValue: inputValue } : {}}
                            // Key Fix #2: Don't automatically select text in input field
                            selectOnFocus={false}
                            // Key Fix #3: Always prevent dropdown from closing when selecting items
                            disableCloseOnSelect={true}
                            renderTags={(value) => {
                                const validSelectedValues = selected || [];
                                return (
                                    (mode === 'Add'
                                        ? validSelectedValues.map(
                                            (selectedvalues, i) =>
                                            (selectedvalues && selectedvalues[optionLabelName] !== "Select All" &&
                                                selectedvalues[optionLabelName] !== "Button" &&
                                                !selectedvalues[optionLabelName]?.startsWith("Clear All") &&
                                                selectedvalues[optionLabelName] !== "ACTION_BUTTONS_ROW" &&
                                                <div key={i} >
                                                    <Chip
                                                        avatar={showAvatarInChip ?
                                                            <Avatar
                                                                style={{
                                                                    width: "24px",
                                                                    height: "24px",
                                                                    fontSize: "14px",
                                                                    color: "#212121",
                                                                    marginLeft: "4px"
                                                                }}
                                                                {...stringAvatar(selectedvalues[optionLabelName])}>
                                                            </Avatar> : null
                                                        }
                                                        key={selectedvalues[optionLabelName] + i}
                                                        label={selectedvalues[optionLabelName]}
                                                        className="oz-customAutoComplete-chip-style"
                                                    />
                                                </div>
                                            )
                                        )
                                        :
                                        (<div style={{
                                            display: "flex",
                                            alignItems: 'center',
                                            alignContent: "center",
                                        }}>
                                            <Typography sx={styleFieldHeader}>
                                                {validSelectedValues.filter(item => item && item[optionLabelName] !== "ACTION_BUTTONS_ROW").map(
                                                    (selectedvalues, index) =>
                                                    (index < 3 && (
                                                        (selectedvalues[optionLabelName] +
                                                            (((validSelectedValues.length - 1) > index) ? ", " : ""))
                                                    ))
                                                )}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                color: "#212121",
                                                paddingRight: '5px',
                                                paddingLeft: "5px"
                                            }}>
                                                {(validSelectedValues.length > 3) ? "..." : ""}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                color: "#99A0A8",
                                            }}>
                                                {(validSelectedValues.length > 3) ? (("+" + (validSelectedValues.length - 3))) : ""}
                                            </Typography>
                                        </div>))
                                );
                            }}
                            role="listbox"
                            onChange={(e, value, reason, details) => {
                                let filteredValue = null
                                if (multiple) {
                                    // Skip the ACTION_BUTTONS_ROW from being selected
                                    const filteredValue = value.filter(option => option[optionLabelName] !== "ACTION_BUTTONS_ROW");

                                    if (filteredValue.find(option => option[objId] === "0_all" || option[objId] === "-1_none")) {
                                        // If "All" or "None" is selected, clear other selections
                                        const singleOption = details?.option;
                                        formik.setFieldValue(name, [singleOption]);
                                        if (additionalProps.onChangeValue) {
                                            additionalProps.onChangeValue.handleChangeDatasList(
                                                additionalProps.onChangeValue.index,
                                                [singleOption],
                                                additionalProps.onChangeValue.createNewDatasList
                                            );
                                        }
                                    }
                                    else if (filteredValue.find((option) => option[objId] === 0)) {
                                        const filteredDatas = inputValue
                                            ? actualDatas.filter(item => item[optionLabelName].toLowerCase().includes(inputValue.toLowerCase()))
                                            : actualDatas;
                                        const newArray = makeNewArray(filteredDatas);
                                        setOldArray(newArray);

                                        formik.setFieldValue(name, newArray);
                                        setSelectAll(true);
                                        if (additionalProps.onChangeValue) {
                                            additionalProps.onChangeValue.handleChangeDatasList(
                                                additionalProps.onChangeValue.index,
                                                newArray,
                                                additionalProps.onChangeValue.createNewDatasList
                                            );
                                        }
                                    } else {
                                        // Skip Clear All option from being added to selection
                                        const hasClearFiltered = filteredValue.some(option =>
                                            option[optionLabelName] && option[optionLabelName].startsWith("Clear All")
                                        );

                                        if (hasClearFiltered) {
                                            // Don't update selection if Clear All was clicked
                                            return;
                                        }

                                        if (additionalProps.onChangeValue) {
                                            additionalProps.onChangeValue.handleChangeDatasList(
                                                additionalProps.onChangeValue.index,
                                                filteredValue,
                                                additionalProps.onChangeValue.createNewDatasList
                                            );
                                        }
                                        formik.setFieldValue(name, filteredValue);
                                    }
                                } else {
                                    if (!(filteredValue[objId] === (actualDatas.length + 1)) && filteredValue[optionLabelName] !== "ACTION_BUTTONS_ROW") {
                                        formik.setFieldValue(name, filteredValue);
                                    }
                                }

                                if (reason === "removeOption" && additionalProps.onChangeValue) {
                                    additionalProps.onChangeValue.handleChangeDatasList(
                                        additionalProps.onChangeValue.index,
                                        [],
                                        reason,
                                        [details.option]
                                    );
                                }
                            }}
                            getOptionLabel={option => option[optionLabelName]}
                            getOptionDisabled={(option) => {
                                if (disableOptionsNotInDatas) {
                                    return !subAdminApiList.some(data => {
                                        if (option["id"] === "0_all" || option["id"] === "-1_none" ||
                                            option["id"] === 0 || option["id"] === "actionButtons") {
                                            return true;
                                        }
                                        return data["id"] === option["id"];
                                    });
                                }
                                return false;
                            }}
                            ListboxProps={{
                                ref: listRef,
                                onScroll: handleScroll,
                                sx: { maxHeight: "280px" }
                            }}
                            onClose={handleClose}
                            PopperComponent={PopperMy}
                            renderOption={(props, option, { selected }) => {
                                // Render the action buttons row
                                if (option[optionLabelName] === "ACTION_BUTTONS_ROW") {
                                    return (
                                        <li {...props}
                                            style={{
                                                padding: 0,
                                                display: 'flex',
                                                flexDirection: 'row',
                                                borderBottom: '1px solid #E5E5E5',
                                                height: 'auto !important'
                                            }}
                                            // These event handlers are critical to prevent the dropdown from closing
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            <Button
                                                sx={{
                                                    textTransform: 'none',
                                                    flex: 1,
                                                    justifyContent: 'center',
                                                    fontFamily: 'Roboto',
                                                    fontWeight: 400,
                                                    color: '#3D8BF8',
                                                    borderRight: '1px solid #E5E5E5',
                                                    minWidth: 'auto',
                                                    borderRadius: 0
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleSelectAll(e);
                                                    return false;
                                                }}
                                                onMouseDown={(e) => e.preventDefault()}
                                            >
                                                Select All
                                            </Button>
                                            <Button
                                                style={{
                                                    textTransform: 'none',
                                                    flex: 1,
                                                    justifyContent: 'center',
                                                    fontFamily: 'Roboto',
                                                    fontWeight: 400,
                                                    color: '#3D8BF8',
                                                    minWidth: 'auto',
                                                    borderRadius: 0
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleClearFiltered(e);
                                                    return false;
                                                }}
                                                onMouseDown={(e) => e.preventDefault()}
                                            >
                                                {"Clear All"}
                                            </Button>
                                        </li>
                                    );
                                }

                                // Skip rendering any special options since we handle them in the header now
                                if (option[optionLabelName] === "Select All" ||
                                    option[optionLabelName]?.startsWith("Clear All")) {
                                    return null;
                                }

                                return (
                                    <Tooltip title={option[optionLabelName]}>
                                        <div
                                            key={`${option[optionLabelName]}-${option[objId]}`}
                                            onClick={() => {
                                                if (option[optionLabelName] === getIn(formik?.values, name)?.name) {
                                                    formik.setFieldValue(name, null);
                                                }
                                            }}
                                            style={{
                                                bgcolor: "#FFFFFF",
                                                position: option[optionLabelName] === "Button" ? 'fixed' : '',
                                                bottom: option[optionLabelName] === "Button" ? '-25px' : '',
                                            }}
                                        >
                                            <li
                                                {...props}
                                                style={{
                                                    height: option[optionLabelName] === "Button" ? "32px" : "36px"
                                                }}
                                                key={`${option[optionLabelName]}-${option[objId]}-li`}
                                            >
                                                {(showCheckBox && (option[optionLabelName] !== "Button")) &&
                                                    <CustomizedCheckbox checked={(option[objId] === 0) ? selectAll : selected} />
                                                }

                                                {(showAvatar && (option[optionLabelName] !== "Button")) &&
                                                    (option[optionLabelName] !== "Select All") &&
                                                    <Avatar
                                                        style={{
                                                            width: "24px",
                                                            height: "24px",
                                                            fontSize: "14px",
                                                            color: "#212121",
                                                            marginLeft: "4px"
                                                        }}
                                                        {...stringAvatar(option[optionLabelName])}
                                                    />
                                                }

                                                {option[optionLabelName] === "Button" ?
                                                    <Button
                                                        sx={{
                                                            fontSize: "14px",
                                                            lineHeight: "16px",
                                                            color: "#3D8BF8",
                                                            fontWeight: '400',
                                                            bgcolor: "#FFFFFF",
                                                            border: "1px solid #E5E5E5",
                                                            borderTopLeftRadius: "0px",
                                                            borderTopRightRadius: "0px",
                                                            marginLeft: "-5px",
                                                            width: `${dimensions.width}px !important`,
                                                            maxWidth: "1000px !important",
                                                            marginTop: "-3px",
                                                            '&:hover': {
                                                                backgroundColor: '#FFFFFF !important',
                                                            }
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            additionalProps.bottomButtonFunction();
                                                        }}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                    >
                                                        {buttonName}
                                                    </Button>
                                                    :
                                                    <Typography sx={listItemStyle}>{option[optionLabelName]}</Typography>
                                                }
                                            </li>

                                            {mode === "Edit" && tempMode === "Edit" &&
                                                getIn(formik?.values, name)?.length > 0 &&
                                                getIn(formik?.values, name)?.length < actualDatas?.length &&
                                                props["data-option-index"] === getIn(formik?.values, name)?.length &&
                                                <div style={{
                                                    border: "1px solid #E6E5E6",
                                                    height: "5px !important",
                                                    marginLeft: "5px"
                                                }} />
                                            }
                                        </div>
                                    </Tooltip>
                                );
                            }}
                            onInputChange={(e, v, reason) => {
                                // Only update input value if it's a user action
                                if (reason === 'input') {
                                    setInputValue(v);
                                }
                            }}
                            renderInput={(params) => (
                                ((tempMode !== 'temp') || (mode === 'Add')) &&
                                <div>
                                    <FormControl
                                        component="fieldset"
                                        error={touch && Boolean(error) ? true : false}
                                        fullWidth
                                    >
                                        <TextField
                                            onClick={changeMode}
                                            sx={{
                                                minWidth: "322px",
                                                paddingRight: tempMode === "temp" ? "" : "10px",
                                                '& .MuiOutlinedInput-root': {
                                                    minHeight: "44px",
                                                    fontSize: "14px",
                                                    color: params.inputProps.value === "" ? "#99A0A8" : "#212121",
                                                    borderRadius: '8px',
                                                },
                                                '& .MuiAutocomplete-input': {
                                                    display: (tempMode === 'temp') && (mode !== 'Add') ? 'none' : 'block'
                                                },
                                                "& .MuiOutlinedInput-root.Mui-focused": {
                                                    "& > fieldset": mode === "Add" ? {
                                                        borderBottomLeftRadius: "0px",
                                                        borderBottomRightRadius: '0px',
                                                        border: "1px solid #E5E5E5"
                                                    } : {
                                                        border: "1px solid #E5E5E5"
                                                    },
                                                },
                                                "& .MuiOutlinedInput-root:hover": {
                                                    "& > fieldset": {
                                                        border: "1px solid #E5E5E5"
                                                    }
                                                }
                                            }}
                                            onKeyDown={(event) => {
                                                if (disableOptionsNotInDatas && event.key === 'Backspace') {
                                                    event.stopPropagation();
                                                }
                                            }}
                                            {...params}
                                            placeholder={(tempMode !== 'temp') || (mode === 'Add') ? placeholder : ""}
                                        />
                                        <FormHelperText>
                                            {touch && Boolean(error) ? error : null}
                                        </FormHelperText>
                                    </FormControl>
                                </div>
                            )}
                            isOptionEqualToValue={(option, value) =>
                                option[optionId || optionLabelName] === value[optionId || optionLabelName]
                            }
                            filterOptions={(options, params) => {
                                // Get filtered options based on current input
                                let filtered = inputValue
                                    ? options.filter(option =>
                                        option[optionLabelName].toLowerCase().includes(params.inputValue.toLowerCase()))
                                    : options;

                                // Remove special options
                                filtered = filtered.filter(option =>
                                    option[objId] !== "0_all" &&
                                    option[objId] !== "-1_none"
                                );

                                // Apply limit for optimization
                                let limitedFiltered = filtered.slice(0, limit);

                                // Add Select All option if needed
                                if (additionalProps.showselectall && filtered.length > 0 && !inputValue) {
                                    limitedFiltered.unshift({ [objId]: 0, [optionLabelName]: "Select All" });
                                }

                                // Add special buttons at the bottom if needed
                                if (showBottomButton && filtered.length > 0) {
                                    limitedFiltered.push({ [objId]: actualDatas.length + 1, [optionLabelName]: "Button" });
                                }

                                // Add All/None options if specified
                                if ((additionalProps.showAllOption || additionalProps.showNoneOption) && !inputValue) {
                                    const specialOptions = [];
                                    if (additionalProps.showAllOption && !selected?.some(option => option[objId] === "0_all")) {
                                        specialOptions.push({ [objId]: "0_all", [optionLabelName]: "All" });
                                    }
                                    if (additionalProps.showNoneOption && !selected?.some(option => option[objId] === "-1_none")) {
                                        specialOptions.push({ [objId]: "-1_none", [optionLabelName]: "None" });
                                    }
                                    limitedFiltered = [...specialOptions, ...limitedFiltered];
                                }

                                // Add the buttons as special options at the top of the list
                                if (additionalProps.showselectall && filtered.length > 0 &&
                                    (inputValue || !additionalProps.showAllOption)) {
                                    // Add a special action buttons option as the first item
                                    limitedFiltered.unshift({
                                        [objId]: "actionButtons",
                                        [optionLabelName]: "ACTION_BUTTONS_ROW"
                                    });
                                }

                                // Add Clear All option if there's a filter and selected items
                                if (inputValue && selected?.length > 0 && filteredData.length > 0) {
                                    const clearAllText = `Clear All (${filteredData.filter(item =>
                                        selected.some(sel => sel[objId] === item[objId])
                                    ).length})`;
                                    limitedFiltered.push({ [objId]: -2, [optionLabelName]: clearAllText });
                                }

                                setSelectAll(selected?.length === filtered?.length);
                                return limitedFiltered;
                            }}
                            {...additionalProps}
                        />
                    </Box>
                </Grid>
                {mode !== 'Add' && (tempMode !== 'Edit') &&
                    <Grid item xs={12}>
                        <TextField
                            onClick={changeMode}
                            sx={{
                                minWidth: "300px",
                                paddingRight: tempMode === "temp" ? "" : "10px",
                                '& .MuiOutlinedInput-root': {
                                    minHeight: "44px",
                                    fontSize: "14px",
                                    borderRadius: '8px',
                                    height: "44px",
                                    width: autocompleteTextFieldWidth || ""
                                },
                                '& .MuiAutocomplete-input': {
                                    display: (tempMode === 'temp') && (mode !== 'Add') ? 'none' : 'block'
                                },
                                "& .MuiOutlinedInput-root.Mui-focused": {
                                    "& > fieldset": mode === "Add" ? {
                                        borderBottomLeftRadius: "0px",
                                        borderBottomRightRadius: '0px',
                                        border: "1px solid #E5E5E5"
                                    } : {
                                        border: "1px solid #E5E5E5"
                                    },
                                },
                                "& .MuiOutlinedInput-root:hover": {
                                    "& > fieldset": {
                                        border: "1px solid #E5E5E5"
                                    }
                                },
                                "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                                    textOverflow: "ellipsis"
                                }
                            }}
                            value={selected?.filter(item => item && item[optionLabelName] !== "ACTION_BUTTONS_ROW").map((item) => item[optionLabelName]).join(', ') || ''}
                            placeholder={placeholder}
                        />
                    </Grid>
                }
            </div>
        );
    }
);

export default CustomAutoCompleteSubAdmin;
